/**
 * Axios API client — attaches Bearer token, handles 401 → refresh → retry.
 *
 * Architecture invariant (CLAUDE.md): all HTTP calls go through this client.
 * Never inline axios.post(...) in business code.
 */

import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';

import { clearAccessToken, getAccessToken, setAccessToken } from '@/lib/auth/tokens';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

/** Create the singleton axios instance. */
export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  withCredentials: true,     // sends the HTTP-only refresh-token cookie automatically
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

/* ---------- Request interceptor — attach Bearer token ---------- */
apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/* ---------- Response interceptor — 401 → refresh → retry once ---------- */
let _isRefreshing = false;
let _refreshQueue: ((token: string) => void)[] = [];

function drainRefreshQueue(token: string): void {
  _refreshQueue.forEach((resolve) => resolve(token));
  _refreshQueue = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) return Promise.reject(error);

    const originalRequest = error.config as AxiosRequestConfig & { _retried?: boolean };

    // Only attempt refresh on 401 and only once per request
    if (error.response?.status !== 401 || originalRequest._retried) {
      return Promise.reject(error);
    }

    // If a refresh is already in flight, queue this request
    if (_isRefreshing) {
      return new Promise<AxiosResponse>((resolve) => {
        _refreshQueue.push((newToken: string) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          resolve(apiClient(originalRequest));
        });
      });
    }

    originalRequest._retried = true;
    _isRefreshing = true;

    try {
      // The refresh endpoint sets a new HTTP-only cookie and returns the new access token.
      // BASE_URL already contains '/api/v1' — don't duplicate the prefix.
      const { data } = await axios.post<{ access_token: string }>(
        `${BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true }
      );

      const newToken = data.access_token;
      setAccessToken(newToken);
      drainRefreshQueue(newToken);

      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
      }
      return apiClient(originalRequest);
    } catch (refreshError) {
      // Refresh failed — clear state and let the caller handle the 401
      clearAccessToken();
      _refreshQueue = [];
      // Redirect to login (avoid circular import by using a browser event)
      window.dispatchEvent(new Event('auth:logout'));
      return Promise.reject(refreshError);
    } finally {
      _isRefreshing = false;
    }
  }
);

export default apiClient;
